import type { BeaconPlan, LessonNode, VisualSpec, LessonImage, GuardianRow, RosterScope, WorkGetInput, AssistantJob, WorkGetOutput, AttentionWork, GuardianInput, StudentRecord, WorkListOutput, LessonScriptV2, EscalationPolicy, ManipulativeSpec, ManipulativeType, LessonTutorBlock, RosterStudentRow, ScoreLedgerOutput, StudentProfileRow, StudentSupportRow, LessonDeliveryMark, WorkListLessonInput, AssistantActionCard, FreeTutoringMastery, StudentProfilePatch, StudentSupportPatch, StudentGuardianView, LessonDeliveryStatus, WorkListSessionInput, AssistantStreamEvent, MasteryResponsiveness, StaffScoreLedgerInput, LessonPlansBoardOutput, StudentDemographicsRow, StandardsCoverageOutput, ListStudentSessionsInput, StudentDemographicsPatch, StudentGuardianLinkPatch, StudentRecordChangeEntry, ClassSessionSummaryOutput, ListStudentSessionsOutput, GetSessionTranscriptOutput, InteractiveResourceContent } from '@classroom-ai/types';

import { auth } from '@classroom-ai/firebase-config';

import { CONFIG } from 'src/global-config';
import { createSseParser } from 'src/lib/sse';

async function getToken(): Promise<string> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}

// Surface the tRPC error message (e.g. "Missing permission: lesson:generate_ai") instead of
// an opaque status. The HTTP error body is `{ error: { message, data: { code } } }`. RBAC
// (epic #143 Phase 5) FORBIDDEN errors flow through here; low priority since the UI gate
// (Phase 4b) hides these actions, but a clear message beats "…failed: 403".
async function errorFrom(res: Response, name: string): Promise<Error> {
  try {
    const body = (await res.json()) as { error?: { message?: string } };
    if (body.error?.message) return new Error(body.error.message);
  } catch {
    /* non-JSON body — fall through */
  }
  return new Error(`tRPC ${name} failed: ${res.status}`);
}

async function callQuery<T>(name: string, input: unknown): Promise<T> {
  const token = await getToken();
  const res = await fetch(
    `${CONFIG.agentUrl}/api/trpc/${name}?input=${encodeURIComponent(JSON.stringify(input))}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw await errorFrom(res, name);
  const body = (await res.json()) as { result: { data: T } };
  return body.result.data;
}

async function callMutation<T>(name: string, input: unknown): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${CONFIG.agentUrl}/api/trpc/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await errorFrom(res, name);
  const body = (await res.json()) as { result: { data: T } };
  return body.result.data;
}

/** The agent has no streaming route (older revision) — the caller falls back to the mutation. */
export class StreamUnavailableError extends Error {
  constructor(public readonly status: number) {
    super(`Streaming unavailable (${status})`);
  }
}

/**
 * Ask AI streaming (#405): POST the question to `/api/assistant/stream` and feed each
 * SSE frame to `onEvent` until the body ends. Rejects with `StreamUnavailableError` when
 * the route doesn't exist (404/405/501), with the server's message on any other non-2xx
 * (401/403/400 are decided BEFORE streaming starts), and with the fetch error if the
 * connection drops mid-stream. Resolves when the stream closes — the caller checks it
 * saw `done`.
 */
async function callStream(path: string, input: unknown, onEvent: (event: AssistantStreamEvent) => void, signal?: AbortSignal): Promise<void> {
  const token = await getToken();
  const res = await fetch(`${CONFIG.agentUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
    signal,
  });
  if (res.status === 404 || res.status === 405 || res.status === 501) throw new StreamUnavailableError(res.status);
  if (!res.ok) throw await errorFrom(res, path);
  if (!res.body) throw new StreamUnavailableError(res.status);
  const parser = createSseParser<AssistantStreamEvent>(onEvent);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true }));
  }
  parser.push(decoder.decode());
  parser.flush();
}

// ─── Response types ────────────────────────────────────────────────────────────

export interface PendingReview {
  eventId:         string;
  prompt:          string;
  studentResponse: string;
  agentScore:      number;
  agentConfidence: number;
  createdAt:       string;
  sessionId:       string;
  /** The session's LangGraph thread (epic #379); absent from agents before rev 00147. */
  threadId?:       string;
  studentUid:      string;
  studentName:     string;
  standardCode:    string;
  standardName:    string;
  standardId:      string;
}

export interface StudentDetailResult {
  transcript: { id: string; role: string; content: string; timestamp: string }[];
  mastery:    { standardCode: string; standardName: string; masteryScore: number; confidence: number; evidenceCount: number; trend: string; flaggedForReview: boolean }[];
  memories:   { memoryId: string; memoryType: string; content: string; salienceScore: number }[];
}

export interface OutlineResult { created: number; alreadyExisted: boolean; total: number }
export interface DraftResult { done: boolean; lessonId?: string; title?: string; remaining: number; confirmedStandards?: string[] }
export interface LessonJob {
  unitId: string;
  total: number;
  completed: number;
  current: string | null;
  status: 'outlined' | 'drafting' | 'completed';
}

export interface AskInput {
  classId: string;
  messages: { role: 'user' | 'assistant'; content: string }[];
  viewContext?: { route?: string; lessonId?: string; unitId?: string; scope?: 'course' | 'class' };
  today?: string;
}

/** Department / Team Lead (epic #566): the caller's Lead access in one school year. */
export interface LeadScope {
  managedCourseIds: string[];
  /** Colleagues' classes the caller can open (never their own). */
  visibleClassIds: string[];
  /** Show the Department nav item. */
  hasLeadAccess: boolean;
}

export interface DepartmentSection {
  classId: string;
  label: string;
  teacherUid: string | null;
  teacherName: string;
  mine: boolean;
  via: 'own' | 'course' | 'teacher' | 'class';
  students: number;
  /** Mean of the class's StandardsMastery rows, null with none. */
  mastery: number | null;
  masteryRows: number;
  alerts: number;
  reviews: number;
  /** Sessions in progress (started in the last 2 h, not ended). */
  liveNow: number;
  /** Lessons ahead (+) / behind (−) of the course baseline before today; null without pacing. */
  pace: number | null;
}

export interface DepartmentCourse {
  courseId: string;
  name: string;
  /** manage = the lead manages the course (edit course tools); view = classes only. */
  grant: 'manage' | 'view';
  why: string;
  sections: DepartmentSection[];
}

export type LeadVisitPage = 'overview' | 'roster' | 'sessions' | 'reviews' | 'student' | 'calendar';

// ─── Routes ───────────────────────────────────────────────────────────────────

export const agentTrpc = {
  assessment: {
    getPendingReviews: (classId: string) =>
      callQuery<{ reviews: PendingReview[] }>('assessment.getPendingReviews', { classId }),

    submitReview: (input: { eventId: string; score: number; note?: string }) =>
      callMutation<{ newMasteryScore: number; masteryTrend: string }>('assessment.submitReview', input),

    injectFollowUp: (input: { threadId: string; question: string }) =>
      callMutation<{ injected: boolean }>('assessment.injectFollowUp', input),
  },

  dashboard: {
    getStudentDetail: (input: { studentId: string; threadId: string }) =>
      callQuery<StudentDetailResult>('dashboard.getStudentDetail', input),

    // A student's past sessions (epic #379) — class-scoped server-side.
    listStudentSessions: (input: ListStudentSessionsInput) =>
      callQuery<ListStudentSessionsOutput>('dashboard.listStudentSessions', input),

    classSessionSummary: (input: { classId: string }) =>
      callQuery<ClassSessionSummaryOutput>('dashboard.classSessionSummary', input),

    getSessionTranscript: (input: { threadId: string }) =>
      callQuery<GetSessionTranscriptOutput>('dashboard.getSessionTranscript', input),

    // "How was this calculated?" (epic #547) — one session's skills or one skill's history.
    scoreLedger: (input: StaffScoreLedgerInput) =>
      callQuery<ScoreLedgerOutput>('dashboard.scoreLedger', input),
  },

  intervention: {
    pauseSession: (input: { threadId: string; reason?: string }) =>
      callMutation<{ pausedAt: string }>('intervention.pauseSession', input),

    acknowledgeAlert: (input: {
      anomalyId: string;
      classId:   string;
      threadId:  string;
      action:    'DISMISSED' | 'REDIRECTED' | 'TOOK_OVER';
      note?:     string;
    }) => callMutation<{ acknowledged: boolean }>('intervention.acknowledgeAlert', input),

    applyScopeOverride: (input: {
      threadId: string;
      scopeOverride: { curriculumScope: 'STRICT' | 'ADJACENT' | 'OPEN'; prereqAccessEnabled: boolean };
    }) => callMutation<{ applied: boolean }>('intervention.applyScopeOverride', input),

    injectPrompt: (input: { threadId: string; prompt: string; type: 'school_staff_message' | 'agent_instruction' }) =>
      callMutation<{ injected: boolean }>('intervention.injectPrompt', input),

    resumeSession: (input: { threadId: string; scopeOverride?: { curriculumScope: 'STRICT' | 'ADJACENT' | 'OPEN'; prereqAccessEnabled: boolean } }) =>
      callMutation<{ resumed: boolean }>('intervention.resumeSession', input),
  },

  lessonPlan: {
    // The Lesson Plans board (epic #474): units + lessons with readiness, pacing dates and
    // exit ticket results (the class's rows; every section in bySection). Read-only.
    board: (input: { courseId: string; classId?: string; today?: string; fresh?: boolean; schedule?: { classId: string } | 'baseline' }) =>
      callQuery<LessonPlansBoardOutput>('lessonPlan.board', input),
    // Board "Fix with AI": store Ask AI proposals (no model call) to approve with
    // classAssistant.executeBatch. Without classId they run under the course's first section.
    proposeFixes: (input: { courseId: string; classId?: string; items: { type: AssistantActionCard['type']; lessonId?: string; unitId?: string }[] }) =>
      callMutation<{ classId: string; cards: AssistantActionCard[]; skipped: { item: { type: string; lessonId?: string }; reason: string }[] }>('lessonPlan.proposeFixes', input),
    // Drag-to-reorder: the unit's full non-archived lesson list in the new order (lesson:manage).
    reorder: (input: { unitId: string; lessonIds: string[] }) =>
      callMutation<{ moved: number }>('lessonPlan.reorder', input),
    outline: (input: { unitId: string; lessonCount?: number; targetMinutes?: number }) =>
      callMutation<OutlineResult>('lessonPlan.outline', input),

    // Unit Assessment (epic #160): generate a linked Pre/Post pair. Idempotent no-op if
    // one exists (created:false). Returns the pair's shared group + lesson ids.
    generateUnitAssessment: (input: { unitId: string; periodMinutes?: number }) =>
      callMutation<{ created: boolean; assessmentGroupId: string; preLessonId: string | null; postLessonId: string | null }>(
        'lessonPlan.generateUnitAssessment',
        input,
      ),

    draftNext: (input: { unitId: string }) =>
      callMutation<DraftResult>('lessonPlan.draftNext', input),

    getJob: (input: { unitId: string }) =>
      callQuery<LessonJob>('lessonPlan.getJob', input),

    clearJob: (input: { unitId: string }) =>
      callMutation<{ ok: boolean }>('lessonPlan.clearJob', input),

    refine: (input: { lessonId: string; instruction: string; currentBody: string | null }) =>
      callMutation<{ body: string; summary: string }>('lessonPlan.refine', input),

    draftTutorBlock: (input: { lessonId: string }) =>
      callMutation<LessonTutorBlock>('lessonPlan.draftTutorBlock', input),

    refineTutorBlock: (input: { lessonId: string; instruction: string; currentBlock: LessonTutorBlock | null }) =>
      callMutation<{ block: LessonTutorBlock; summary: string }>('lessonPlan.refineTutorBlock', input),

    draftQuiz: (input: { lessonId: string; count: number; description?: string }) =>
      callMutation<InteractiveResourceContent>('lessonPlan.draftQuiz', input),

    // AI-taught full lesson (epic #318). Persists; with a saved script it returns
    // { status: 'exists' } unless `overwrite` (the UI confirms first). Always v2 (epic #363).
    draftScript: (input: { lessonId: string; overwrite?: boolean; instruction?: string }) =>
      callMutation<{ status: 'created' | 'exists'; script: LessonScriptV2 }>('lessonPlan.draftScript', input),

    // Draw a math visual for one point/question (epic #318 Phase 3); NOT persisted.
    draftVisual: (input: { lessonId: string; concept: string; text: string }) =>
      callMutation<{ spec: VisualSpec | null }>('lessonPlan.draftVisual', input),

    // Generate an illustration (epic #318 Phase 6) — stored PENDING; NOT persisted in the script.
    draftImage: (input: { lessonId: string; concept: string; text: string; instruction?: string; inDraft?: number }) =>
      callMutation<{ image: LessonImage | null }>('lessonPlan.draftImage', input),

    // Draft a we-do manipulative (epic #318 Phase 5); NOT persisted.
    draftManipulative: (input: { lessonId: string; concept: string; text: string; answer?: string; preferType?: ManipulativeType }) =>
      callMutation<{ spec: ManipulativeSpec | null }>('lessonPlan.draftManipulative', input),

    // Record narration for new / edited text of the SAVED script (epic #318 Phase 4).
    narrate: (input: { lessonId: string }) =>
      callMutation<{ script: LessonScriptV2; generated: number; reused: number; failed: number; total: number }>('lessonPlan.narrate', input),

    // Rewrite one part of a v2 draft; NOT persisted — the editor swaps it in.
    refineScriptSegment: (input: { lessonId: string; script: LessonScriptV2; segmentId: string; instruction: string }) =>
      callMutation<LessonNode>('lessonPlan.refineScriptSegment', input),
  },

  // Teacher-assigned make-up lessons (epic #318). Reads use ListLessonAssignmentsByClass.
  makeup: {
    assign: (input: { classId: string; lessonPlanId: string; studentUids: string[]; dueDate?: string | null }) =>
      callMutation<{ assigned: number }>('makeup.assign', input),
    unassign: (input: { classId: string; lessonPlanId: string; studentUid: string }) =>
      callMutation<{ removed: boolean }>('makeup.unassign', input),
  },

  // "Mark taught" (epic #647): the class teacher or a Department Head records whether a
  // lesson was taught. No marking before the lesson's day; notes only for the teacher, the
  // course's Department Heads and ED360 (`canReadNotes`). Never write ClassLessonDelivery directly.
  lessonDelivery: {
    mark: (input: { classId: string; lessonPlanId: string; status: LessonDeliveryStatus; taughtOn?: string | null; notes?: string | null; tags?: string[] | null; carryOver?: boolean }) =>
      callMutation<LessonDeliveryMark>('lessonDelivery.mark', input),
    clear: (input: { classId: string; lessonPlanId: string }) => callMutation<{ cleared: boolean }>('lessonDelivery.clear', input),
    listForClass: (input: { classId: string }) =>
      callQuery<{ marks: LessonDeliveryMark[]; marksFrom: string | null; canReadNotes: boolean; canMark: boolean; today: string }>('lessonDelivery.listForClass', input),
    unmarked: (input: { classIds: string[] }) =>
      callQuery<{ today: string; lessons: { classId: string; lessonPlanId: string; unitId: string | null; title: string; kind: string | null; date: string; today: boolean }[] }>('lessonDelivery.unmarked', input),
  },

  // Per-class mastery scoring (epic #440): a server-side merge into Class.escalationPolicy,
  // gated on class:configure_mastery. Never write that column from the teacher app directly.
  classConfig: {
    getMasteryPolicy: (input: { classId: string }) =>
      callQuery<{ stored: Partial<EscalationPolicy> | null; effective: EscalationPolicy }>('classConfig.getMasteryPolicy', input),
    updateMasteryPolicy: (input: { classId: string; masteryResponsiveness?: MasteryResponsiveness; masteryPriorWeight?: number; freeTutoringMastery?: FreeTutoringMastery }) =>
      callMutation<{ stored: Partial<EscalationPolicy>; effective: EscalationPolicy }>('classConfig.updateMasteryPolicy', input),
  },

  // Ask AI (epic #391): free-form, read-only questions about one class; answers are
  // Markdown with in-app lesson links. 5–40 s typical. `askStream` (#405) is the drawer's
  // first choice — tool status + answer text as they happen, ending with the same payload
  // `ask` returns; `ask` is the request/response fallback.
  classAssistant: {
    askStream: (input: AskInput, onEvent: (event: AssistantStreamEvent) => void, signal?: AbortSignal) =>
      callStream('/api/assistant/stream', input, onEvent, signal),

    ask: (input: AskInput) =>
      callMutation<{
        answer: string;
        rounds: number;
        toolCalls: { name: string; ok: boolean }[];
        /** Proposed fixes (Phase E) — shown as cards; nothing has run yet. */
        actions?: AssistantActionCard[];
      }>('classAssistant.ask', input),

    // Approve proposed action(s): the server re-checks access + AI permissions and queues a
    // background job (Phase G) — poll getJob. `replace` = the teacher OK'd overwriting.
    executeAction: (input: { actionId: string; replace?: boolean }) =>
      callMutation<{ jobId: string }>('classAssistant.executeAction', input),
    executeBatch: (input: { actionIds: string[] }) =>
      callMutation<{ jobId: string }>('classAssistant.executeBatch', input),
    getJob: (input: { jobId: string }) => callQuery<AssistantJob>('classAssistant.getJob', input),
    revertAction: (input: { actionId: string }) =>
      callMutation<{ message: string }>('classAssistant.revertAction', input),
  },

  // Standards coverage (epic #453): server-computed lesson-plan coverage per standard —
  // the course plan, or one class of it (`classId`) with taught / behind / upcoming states.
  standards: {
    coverage: (input: { courseId: string; classId?: string; today?: string; fresh?: boolean }) =>
      callQuery<StandardsCoverageOutput>('standards.coverage', input),
  },

  // Class overview (epic #575): open make-ups / focus practice with their standards, for
  // the Needs attention card's overdue and "In hand" rules.
  classOverview: {
    openWork: (input: { classId: string }) => callQuery<{ work: AttentionWork[] }>('classOverview.openWork', input),
    // Clear with Beacon (P4/P5): build a plan (stores proposals), edit it, run the ticked steps.
    proposePlan: (input: { classId: string; today?: string }) => callMutation<BeaconPlan>('classOverview.proposePlan', input),
    adjustPlanSteps: (input: { classId: string; changes: { actionId: string; dueDate?: string | null; score?: 0 | 0.5 | 1 }[]; today?: string }) =>
      callMutation<{ updated: number }>('classOverview.adjustPlanSteps', input),
    executePlan: (input: { actionIds: string[] }) => callMutation<{ jobId: string }>('classOverview.executePlan', input),
  },

  // Focus practice on one skill (epic #575, #583) — class teacher + pacing:manage_class.
  practice: {
    assign: (input: { classId: string; standardId: string; studentUids: string[]; dueDate?: string | null; targetAnswers?: number }) =>
      callMutation<{ assigned: number }>('practice.assign', input),
    unassign: (input: { classId: string; standardId: string; studentUid: string }) =>
      callMutation<{ removed: boolean }>('practice.unassign', input),
    listForClass: (input: { classId: string }) => callQuery<{ work: AttentionWork[] }>('practice.listForClass', input),
  },

  // Department / Team Lead (epic #566) — self-scoped to the caller's Lead access grants.
  department: {
    myScope: (input: { schoolYearId: string; fresh?: boolean }) =>
      callQuery<LeadScope>('department.myScope', input),
    overview: (input: { schoolYearId: string; today: string; fresh?: boolean }) =>
      callQuery<{ courses: DepartmentCourse[] }>('department.overview', input),
    recordVisit: (input: { classId: string; page: LeadVisitPage }) =>
      callMutation<{ recorded: boolean }>('department.recordVisit', input),
  },

  tutoring: {
    openWindow: (input: { classId: string; lessonPlanId: string }) =>
      callMutation<{ opened: boolean }>('tutoring.openWindow', input),

    closeWindow: (input: { classId: string }) =>
      callMutation<{ closed: boolean }>('tutoring.closeWindow', input),
  },

  // Student records (epic #595): profile, supports, demographics, guardians, photo, history.
  // The one enforcement + audit path — the teacher app never writes these tables directly.
  // A class scope: the caller must see the class and the student must be enrolled in it.
  roster: {
    classSummary: (input: { classId: string }) =>
      callQuery<{ students: RosterStudentRow[] }>('roster.classSummary', input),
    getStudent: (input: { scope: RosterScope; uid: string; includeDemographics?: boolean }) =>
      callQuery<StudentRecord>('roster.getStudent', input),
    updateProfile: (input: { scope: RosterScope; uid: string; patch: StudentProfilePatch; override?: boolean }) =>
      callMutation<{ changed: string[]; profile: StudentProfileRow | null }>('roster.updateProfile', input),
    updateSupport: (input: { scope: RosterScope; uid: string; patch: StudentSupportPatch; override?: boolean }) =>
      callMutation<{ changed: string[]; support: StudentSupportRow | null }>('roster.updateSupport', input),
    updateDemographics: (input: { scope: RosterScope; uid: string; patch: StudentDemographicsPatch; override?: boolean }) =>
      callMutation<{ changed: string[]; demographics: StudentDemographicsRow | null }>('roster.updateDemographics', input),
    setPhoto: (input: { scope: RosterScope; uid: string; photoPath: string }) =>
      callMutation<{ profile: StudentProfileRow | null }>('roster.setPhoto', input),
    removePhoto: (input: { scope: RosterScope; uid: string }) =>
      callMutation<{ profile: StudentProfileRow | null }>('roster.removePhoto', input),
    listGuardians: (input: { scope: RosterScope; uid: string }) =>
      callQuery<{ guardians: StudentGuardianView[] }>('roster.listGuardians', input),
    findGuardians: (input: { scope: RosterScope; uid: string; email?: string; phone?: string }) =>
      callQuery<{ guardians: GuardianRow[] }>('roster.findGuardians', input),
    upsertGuardian: (input: { scope: RosterScope; uid: string; guardianId?: string; guardian?: Partial<GuardianInput>; link?: StudentGuardianLinkPatch; override?: boolean }) =>
      callMutation<{ guardianId: string; guardians: StudentGuardianView[] }>('roster.upsertGuardian', input),
    unlinkGuardian: (input: { scope: RosterScope; uid: string; guardianId: string }) =>
      callMutation<{ guardianDeleted: boolean; guardians: StudentGuardianView[] }>('roster.unlinkGuardian', input),
    listChanges: (input: { scope: RosterScope; uid: string; limit?: number }) =>
      callQuery<{ changes: StudentRecordChangeEntry[] }>('roster.listChanges', input),
  },
  // Scratch pad (epic #312): students' drawn work — the class teacher and ed360 staff.
  work: {
    listSession: (input: WorkListSessionInput) => callMutation<WorkListOutput>('work.listSession', input),
    listLesson: (input: WorkListLessonInput) => callMutation<WorkListOutput>('work.listLesson', input),
    get: (input: WorkGetInput) => callMutation<WorkGetOutput>('work.get', input),
  },
};
